import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import RichTextEditor from './RichTextEditor';
import { LANGUAGES, fieldSuffix } from '../../i18n/languages';

import { ta } from '../../i18n/adminT';
const emptyTemplate = { subjectIt: '', bodyHtmlIt: '', subjectEn: '', bodyHtmlEn: '', subjectRo: '', bodyHtmlRo: '' };

// Suggerimenti mostrati nei campi vuoti, nella lingua della scheda attiva
const PLACEHOLDERS = {
  it: { subject: 'Benvenuto/a — registrazione confermata', body: 'Ciao {{nome}} {{cognome}}, la tua registrazione è stata completata…' },
  en: { subject: 'Welcome — your registration is confirmed', body: 'Hi {{nome}} {{cognome}}, your registration has been completed…' },
  ro: { subject: 'Bun venit — înregistrarea a fost confirmată', body: 'Bună ziua, {{nome}} {{cognome}}, înregistrarea dumneavoastră a fost finalizată…' },
};

export default function WelcomeEmailSettings() {
  const [template, setTemplate] = useState(emptyTemplate);
  const [activeLang, setActiveLang] = useState('it');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const [badgeEmailEnabled, setBadgeEmailEnabled] = useState(false);
  const [badgeEmailSaving, setBadgeEmailSaving] = useState(false);
  const [badgeEmailError, setBadgeEmailError] = useState('');

  useEffect(() => {
    api.getWelcomeEmailTemplate()
      .then((res) => setTemplate({ ...emptyTemplate, ...res.data }))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.getBadgeCheckinEmailConfig()
      .then((res) => setBadgeEmailEnabled(Boolean(res.data?.enabled)))
      .catch(() => {});
  }, []);

  const handleToggleBadgeEmail = async (e) => {
    const next = e.target.checked;
    setBadgeEmailEnabled(next); // ottimistico, corretto sotto se il salvataggio fallisce
    setBadgeEmailError('');
    setBadgeEmailSaving(true);
    try {
      await api.saveBadgeCheckinEmailConfig(next);
    } catch (err) {
      setBadgeEmailEnabled(!next);
      setBadgeEmailError(err.message);
    } finally {
      setBadgeEmailSaving(false);
    }
  };

  const updateSubject = (e) => {
    setSaved(false);
    setTemplate((t) => ({ ...t, [`subject${fieldSuffix(activeLang)}`]: e.target.value }));
  };

  const updateBody = (html) => {
    setSaved(false);
    setTemplate((t) => ({ ...t, [`bodyHtml${fieldSuffix(activeLang)}`]: html }));
  };

  const handleSave = async () => {
    setError('');
    setSaved(false);
    setSaving(true);
    try {
      await api.saveWelcomeEmailTemplate(template);
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const subjectValue = template[`subject${fieldSuffix(activeLang)}`] || '';
  const bodyValue = template[`bodyHtml${fieldSuffix(activeLang)}`] || '';

  return (
    <div>
      <h1 className="admin-page-title">{ta('Email di benvenuto')}</h1>
      <p className="admin-page-subtitle">
        {ta('Testo inviato ai visitatori che lasciano la propria email in fase di registrazione, insieme ai PDF di privacy e sicurezza.')}
      </p>

      <div className="admin-card" style={{ maxWidth: 640 }}>
        <h3 style={{ marginBottom: 10 }}>{ta('Invio a ogni check-in badge')}</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--muted-text)', marginBottom: 14 }}>
          {ta('Se attivo, questa stessa email (con privacy e sicurezza allegate) viene inviata anche agli')} <strong>{ta('utenti esterni')}</strong> {ta('che hanno un\'email impostata, ogni volta che registrano l\'ingresso con il badge — non solo ai visitatori occasionali che la lasciano nel modulo "Registrati".')}
        </p>
        {badgeEmailError && <div className="form-error">{badgeEmailError}</div>}
        <label className="checkbox-row">
          <input type="checkbox" checked={badgeEmailEnabled} onChange={handleToggleBadgeEmail} disabled={badgeEmailSaving} />
          <span>{ta('Invia privacy e sicurezza agli utenti esterni a ogni check-in badge')}</span>
        </label>
      </div>

      <div className="admin-card" style={{ maxWidth: 640 }}>
        {error && <div className="form-error">{error}</div>}
        {saved && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {ta('Impostazioni salvate.')}
          </div>
        )}

        <div className="btn-row" style={{ marginBottom: 18 }}>
          {LANGUAGES.map(({ key, label, flag }) => (
            <button
              key={key}
              type="button"
              className={activeLang === key ? 'btn btn-primary' : 'btn btn-ghost'}
              onClick={() => setActiveLang(key)}
            >
              {flag} {label}
            </button>
          ))}
        </div>

        <div className="field">
          <label>{ta('Oggetto')}</label>
          <input
            type="text"
            value={subjectValue}
            onChange={updateSubject}
            maxLength={300}
            placeholder={PLACEHOLDERS[activeLang].subject}
          />
        </div>

        <div className="field">
          <label>{ta('Testo email')}</label>
          <RichTextEditor
            key={activeLang}
            value={bodyValue}
            onChange={updateBody}
            placeholder={PLACEHOLDERS[activeLang].body}
          />
        </div>

        <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginBottom: 10 }}>
          {ta('Puoi usare')} <span className="mono">{ta('{{nome}}')}</span> {ta('e')} <span className="mono">{ta('{{cognome}}')}</span> {ta('nel testo: verranno sostituiti con i dati della persona registrata. Se lasci oggetto o testo vuoti per una lingua, verrà usato un testo predefinito.')}
        </p>
        <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginBottom: 18 }}>
          {ta('Le immagini (es. il logo nella firma aziendale) vengono incorporate direttamente nel testo dell\'email: funzionano nella maggior parte dei client di posta, ma preferisci sempre un\'immagine piccola e leggera (max 200 KB) — alcuni client aziendali più datati potrebbero non mostrare immagini incorporate in questo modo.')}
        </p>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
          style={{ width: 'auto', padding: '10px 20px' }}
        >
          {saving ? ta('Salvataggio…') : ta('Salva')}
        </button>
      </div>
    </div>
  );
}
